import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Upload,
  message,
  Popconfirm,
  Tag,
  Avatar,
  Image
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UploadOutlined,
  HeartOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import adminNgoService from '../../services/adminNgoService';
import BackButton from '../../components/BackButton';

const { TextArea } = Input;

const NgoList = () => {
  const { t } = useTranslation();
  const [ngos, setNgos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNgo, setEditingNgo] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    fetchNgos();
  }, []);

  const fetchNgos = async () => {
    setLoading(true);
    const result = await adminNgoService.getNgos();
    if (result.success) {
      setNgos(result.data.results || result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingNgo(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setFileList([]);
    setPreviewImage('');
    setModalVisible(true);
  };

  const handleEdit = (ngo) => {
    setEditingNgo(ngo);
    form.setFieldsValue({
      name_en: ngo.name_en,
      name_el: ngo.name_el,
      description_en: ngo.description_en,
      description_el: ngo.description_el,
      website: ngo.website,
      is_active: ngo.is_active
    });
    setFileList([]);
    // Set preview image if exists
    if (ngo.logo?.full_size) {
      setPreviewImage(ngo.logo.full_size);
    } else if (ngo.logo_url) {
      setPreviewImage(ngo.logo_url);
    } else {
      setPreviewImage('');
    }
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const result = await adminNgoService.deleteNgo(id);
    if (result.success) {
      message.success(t('admin.ngos.deleteSuccess'));
      fetchNgos();
    } else {
      message.error(result.error);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.ngos.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.ngos.nameEl'), dataIndex: 'name_el' },
      { title: t('admin.ngos.descriptionEn'), dataIndex: 'description_en' },
      { title: t('admin.ngos.descriptionEl'), dataIndex: 'description_el' },
      { title: t('admin.ngos.website'), dataIndex: 'website' },
      {
        title: t('common.status'),
        dataIndex: 'is_active',
        exportRender: (val) => val ? t('common.active') : t('common.inactive')
      }
    ];
    exportToExcel(ngos, exportColumns, 'ngos', t('admin.ngos.title'));
    message.success(t('admin.ngos.exportSuccess'));
  };

  const handleSubmit = async (values) => {
    const data = { ...values };

    // Add logo file if selected
    if (fileList.length > 0 && fileList[0].originFileObj) {
      data.logo = fileList[0].originFileObj;
    }

    const result = editingNgo
      ? await adminNgoService.updateNgo(editingNgo.id, data)
      : await adminNgoService.createNgo(data);

    if (result.success) {
      message.success(editingNgo ? t('admin.ngos.updateSuccess') : t('admin.ngos.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      setPreviewImage('');
      fetchNgos();
    } else {
      const errorMsg = typeof result.error === 'object'
        ? Object.values(result.error).flat().join(', ')
        : result.error;
      message.error(errorMsg);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(t('admin.ngos.imageOnlyError'));
        return Upload.LIST_IGNORE;
      }
      if (file.size > 5 * 1024 * 1024) {
        message.error(t('admin.ngos.imageSizeError'));
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      // Preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
      return false;
    },
    fileList,
    accept: 'image/*',
    maxCount: 1
  };

  const columns = [
    {
      title: t('admin.ngos.logo'),
      dataIndex: 'logo',
      key: 'logo',
      width: 80,
      render: (logo) => {
        const logoUrl = logo?.thumbnail || logo?.full_size;
        return logoUrl ? (
          <Avatar src={logoUrl} size={50} shape="square" />
        ) : (
          <Avatar icon={<HeartOutlined />} size={50} shape="square" style={{ backgroundColor: '#eb2f96' }} />
        );
      }
    },
    {
      title: t('admin.ngos.nameEn'),
      dataIndex: 'name_en',
      key: 'name_en',
      sorter: (a, b) => a.name_en.localeCompare(b.name_en)
    },
    {
      title: t('admin.ngos.nameEl'),
      dataIndex: 'name_el',
      key: 'name_el'
    },
    {
      title: t('admin.ngos.website'),
      dataIndex: 'website',
      key: 'website',
      render: (website) => website ? (
        <a href={website} target="_blank" rel="noopener noreferrer">
          <GlobalOutlined /> {t('common.view')}
        </a>
      ) : '-'
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
      filters: [
        { text: t('common.active'), value: true },
        { text: t('common.inactive'), value: false }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('admin.ngos.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <Space>
            <HeartOutlined style={{ color: '#eb2f96' }} />
            <span>{t('admin.ngos.title')}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!ngos.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchNgos}>
              {t('common.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('admin.ngos.addNgo')}
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('admin.ngos.description')}
        </p>
        <Table
          columns={columns}
          dataSource={ngos}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('admin.ngos.totalNgos', { total })
          }}
        />
      </Card>

      <Modal
        title={editingNgo ? t('admin.ngos.editNgo') : t('admin.ngos.addNgo')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFileList([]);
          setPreviewImage('');
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="name_en"
            label={t('admin.ngos.nameEn')}
            rules={[
              { required: true, message: t('admin.ngos.nameEnRequired') },
              { max: 200, message: t('admin.ngos.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.ngos.nameEnPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('admin.ngos.nameEl')}
            rules={[
              { required: true, message: t('admin.ngos.nameElRequired') },
              { max: 200, message: t('admin.ngos.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.ngos.nameElPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description_en"
            label={t('admin.ngos.descriptionEn')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.ngos.descriptionEnPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="description_el"
            label={t('admin.ngos.descriptionEl')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.ngos.descriptionElPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="website"
            label={t('admin.ngos.website')}
            rules={[
              { type: 'url', message: t('admin.ngos.websiteInvalid') }
            ]}
          >
            <Input placeholder={t('admin.ngos.websitePlaceholder')} prefix={<GlobalOutlined />} />
          </Form.Item>

          <Form.Item
            label={t('admin.ngos.logo')}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {previewImage && (
                <Image
                  src={previewImage}
                  alt="Logo preview"
                  style={{ maxWidth: 150, maxHeight: 150, marginBottom: 8 }}
                />
              )}
              <Upload {...uploadProps} listType="picture">
                <Button icon={<UploadOutlined />}>
                  {editingNgo ? t('admin.ngos.changeLogo') : t('admin.ngos.uploadLogo')}
                </Button>
              </Upload>
            </Space>
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingNgo ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
                setFileList([]);
                setPreviewImage('');
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NgoList;
